import type { LeadJourneyLink, LeadJourneyResponse } from "api/crm-analytics";

export type JourneyNodeKind = "source" | "milestone" | "outcome";

export type JourneyNode = {
  id: string;
  kind: JourneyNodeKind;
  label: string;
  count: number;
  // Position in the milestone order. Sources sit before the first milestone;
  // an outcome carries the rank of the milestone it branched off.
  rank: number;
  // Share of the step before this one — the number shown on the pill. The
  // first milestone and the source bands have no previous step, so they carry
  // null and render without a percentage.
  rateOfPrevious: number | null;
};

/**
 * What a drill-down was opened for — a bar or a ribbon.
 *
 * `id` goes back to the API untouched: the endpoint resolves a bar id and a
 * ribbon id by the same bitmask rules that drew the chart, so the list length
 * always matches the number that was clicked.
 */
export type JourneySelection = {
  id: string;
  label: string;
  count: number;
};

export type JourneyGraph = {
  total: number;
  nodes: JourneyNode[];
  sources: JourneyNode[];
  milestones: JourneyNode[];
  outcomes: JourneyNode[];
  links: LeadJourneyLink[];
  nodeById: Map<string, JourneyNode>;
  // Every ribbon by its own id, so a hover resolves one without scanning.
  linkById: Map<string, LeadJourneyLink>;
  // Links leaving / entering a node, for the hover highlight walk.
  outgoing: Map<string, LeadJourneyLink[]>;
  incoming: Map<string, LeadJourneyLink[]>;
};

const safeRate = (value: number, base: number): number | null =>
  base > 0 ? value / base : null;

const pushTo = <T>(map: Map<string, T[]>, key: string, value: T) => {
  const existing = map.get(key);
  if (existing) existing.push(value);
  else map.set(key, [value]);
};

/**
 * Normalizes the API response into the shape the layout and the interactions
 * both read: every node indexed by id, plus the adjacency the hover highlight
 * walks. The server already ordered the sources and outcomes, so nothing here
 * re-sorts them.
 */
export const buildJourneyGraph = (data: LeadJourneyResponse): JourneyGraph => {
  const countByMilestone = new Map(
    data.milestones.map((m) => [m.key, m.count])
  );

  const milestones: JourneyNode[] = data.milestones.map((m) => ({
    id: m.id,
    kind: "milestone",
    label: m.label,
    count: m.count,
    rank: m.rank,
    rateOfPrevious:
      m.rank === 0
        ? null
        : safeRate(m.count, data.milestones[m.rank - 1]?.count ?? 0)
  }));

  const sources: JourneyNode[] = data.sources.map((s) => ({
    id: s.id,
    kind: "source",
    label: s.label,
    count: s.count,
    rank: -1,
    rateOfPrevious: null
  }));

  const outcomes: JourneyNode[] = data.outcomes.map((o) => ({
    id: o.id,
    kind: "outcome",
    label: o.label,
    count: o.count,
    rank: o.after_rank,
    rateOfPrevious: safeRate(
      o.count,
      countByMilestone.get(o.after_milestone) ?? 0
    )
  }));

  const nodes = [...sources, ...milestones, ...outcomes];
  const nodeById = new Map(nodes.map((n) => [n.id, n]));

  const outgoing = new Map<string, LeadJourneyLink[]>();
  const incoming = new Map<string, LeadJourneyLink[]>();
  // A link naming a node the response never sent would break the layout, so
  // drop it rather than render a ribbon that leads nowhere.
  const links = data.links.filter(
    (l) => nodeById.has(l.from_id) && nodeById.has(l.to_id)
  );
  links.forEach((link) => {
    pushTo(outgoing, link.from_id, link);
    pushTo(incoming, link.to_id, link);
  });

  return {
    total: data.total_leads,
    nodes,
    sources,
    milestones,
    outcomes,
    links,
    nodeById,
    linkById: new Map(links.map((l) => [linkId(l), l])),
    outgoing,
    incoming
  };
};

/**
 * What lights up when the pointer lands on `id`, or `null` for "light nothing
 * and dim nothing".
 *
 * Upstream is traced all the way back; downstream never goes past the next
 * bar. That asymmetry is forced by the data, not a preference. The links carry
 * aggregate counts, and the flow re-merges — Application started takes 19
 * leads from a skip and 5 by way of Contacted, and nothing records which of
 * those 24 went on to submit. So tracing back from a ribbon answers "where did
 * these leads come from", while carrying on forwards would light a 19-lead
 * stall band as though it belonged to the 5 leads under the pointer. A node's
 * own outgoing ribbons are safe, because those carry exactly that node's
 * leads; it is the hop after them that starts claiming leads this hover does
 * not own.
 */
export const collectHighlight = (
  graph: JourneyGraph,
  id: string
): Set<string> | null => {
  const highlighted = new Set<string>([id]);

  // Carries on through the source ribbons into the channels themselves, so
  // tracing back from anywhere reaches the whole of where those leads came
  // from. Hovering a source's *own* node or ribbon does not start a walk at
  // all (see below) — that is the case where lighting the siblings would be
  // wrong, not this one.
  const walkUp = (nodeId: string) => {
    (graph.incoming.get(nodeId) ?? []).forEach((l) => {
      if (highlighted.has(linkId(l))) return;
      highlighted.add(linkId(l));
      highlighted.add(l.from_id);
      walkUp(l.from_id);
    });
  };

  const stepDown = (nodeId: string) => {
    (graph.outgoing.get(nodeId) ?? []).forEach((l) => {
      highlighted.add(linkId(l));
      highlighted.add(l.to_id);
    });
  };

  const link = graph.linkById.get(id);
  if (link) {
    highlighted.add(link.from_id);
    highlighted.add(link.to_id);
    // A source ribbon has nothing to trace back to: every source lands on the
    // first milestone and they are indistinguishable from there on.
    if (link.kind !== "source") walkUp(link.from_id);
    return highlighted;
  }

  const node = graph.nodeById.get(id);
  if (!node) return null;

  stepDown(id);
  if (node.kind !== "source") walkUp(id);

  // A milestone nobody reached carries no ribbons in or out, so there is no
  // path to light. Returning the bare node would dim every other thing on the
  // chart in favour of a 2px tick — the hover would read as the chart going
  // blank. Nothing highlights instead.
  return highlighted.size > 1 ? highlighted : null;
};

export const linkId = (link: LeadJourneyLink): string =>
  `${link.from_id}>${link.to_id}>${link.kind}`;
